import { useQuery } from '@tanstack/react-query';

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Close } from '@mui/icons-material';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import { ContentClient } from '@@api';
import { formatTimestampFull, useApiClient } from '@@common';

import { StringTemplateViewModel } from '../../api/api';

const pageSizeOptions = [10, 20, 50];
const initialPaginationModel: GridPaginationModel = {
	pageSize: pageSizeOptions[1],
	page: 0,
};

type ContentTemplateVersionsDialogProps = {
	template: StringTemplateViewModel | null;
	changeSetNameById: Map<string, string>;
	onClose: () => void;
};

export default function ContentTemplateVersionsDialog({ template, changeSetNameById, onClose }: ContentTemplateVersionsDialogProps) {
	const client = useApiClient(ContentClient);
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(initialPaginationModel);

	useEffect(() => {
		setPaginationModel(initialPaginationModel);
	}, [template?.templateId]);

	const { data, isLoading } = useQuery({
		queryKey: ['content', 'templates', 'versions', template?.templateId, paginationModel],
		queryFn: () => client.searchVersions(template!.templateId!, paginationModel.page, paginationModel.pageSize),
		enabled: Boolean(template?.templateId),
	});

	const rowCount = useMemo(
		() => (data ? paginationModel.page * paginationModel.pageSize + (data.hasNextPage ? paginationModel.pageSize + 1 : data.items.length) : 0),
		[paginationModel, data],
	);

	const columns: GridColDef<StringTemplateViewModel>[] = [
		{
			field: 'versionId',
			headerName: 'Version',
			width: 100,
			sortable: false,
			renderCell: (params) => <Link to={`/content/${params.row.id}`}>{params.value}</Link>,
		},
		{
			field: 'name',
			headerName: 'Name',
			flex: 1,
			sortable: false,
		},
		{
			field: 'changeSetId',
			headerName: 'Change Set',
			flex: 1,
			sortable: false,
			valueGetter: (value: string) => changeSetNameById.get(value) ?? value,
		},
		{
			field: 'updatedByName',
			headerName: 'Modified By',
			flex: 1,
			sortable: false,
			valueGetter: (_value, row) => row.updatedByName ?? row.updatedById ?? row.createdByName ?? row.createdById ?? '',
		},
		{
			field: 'updatedDate',
			headerName: 'Modified',
			flex: 1,
			sortable: false,
			valueFormatter: (value: string) => (value ? formatTimestampFull(value) : ''),
		},
		{
			field: 'previousTemplateVersionId',
			headerName: 'Previous ID',
			flex: 1,
			sortable: false,
		},
	];

	return (
		<Dialog open={Boolean(template)} onClose={onClose} maxWidth="lg" fullWidth>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
				{template ? `${template.name} Versions` : 'Template Versions'}
				<IconButton aria-label="Close" onClick={onClose} size="small">
					<Close fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ height: 520 }}>
				<DataGrid
					loading={isLoading}
					rows={data?.items ?? []}
					rowCount={rowCount}
					columns={columns}
					paginationMode="server"
					sortingMode="client"
					disableColumnFilter
					pageSizeOptions={pageSizeOptions}
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					sx={{ border: 0 }}
				/>
			</DialogContent>
		</Dialog>
	);
}
